import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { createContext, useContext, useMemo, useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { submitFeatureRequest, pickAndUploadFeedbackScreenshot } from '@/lib/featureRequests';
import { getErrorMessage } from '@/lib/functions';

type ModalState = 'idle' | 'sending' | 'done';

type BugReportContextValue = {
  /** Ανοίγει το modal υποβολής bug/βελτίωσης από οπουδήποτε στην εφαρμογή.
   * `prefillText` προσυμπληρώνει το κείμενο (π.χ. με το μήνυμα σφάλματος
   * που πάτησε ο χρήστης) ώστε να μην χρειάζεται να το ξαναγράψει. */
  open: (prefillText?: string) => void;
};

const BugReportContext = createContext<BugReportContextValue | null>(null);

export function useBugReport(): BugReportContextValue {
  const ctx = useContext(BugReportContext);
  if (!ctx) throw new Error('useBugReport must be used within BugReportProvider');
  return ctx;
}

export function BugReportProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [visible, setVisible] = useState(false);
  const [featureText, setFeatureText] = useState('');
  const [featureState, setFeatureState] = useState<ModalState>('idle');
  const [screenshotPath, setScreenshotPath] = useState<string | null>(null);
  const [screenshotBusy, setScreenshotBusy] = useState(false);
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function open(prefillText?: string) {
    setFeatureText(prefillText ?? '');
    setFeatureState('idle');
    setScreenshotPath(null);
    setVisible(true);
  }

  function close() {
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    setVisible(false);
  }

  async function pickScreenshot() {
    if (!user) return;
    setScreenshotBusy(true);
    try {
      const path = await pickAndUploadFeedbackScreenshot(user.id);
      if (path) setScreenshotPath(path);
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('settings.upload_error_default')));
    } finally {
      setScreenshotBusy(false);
    }
  }

  async function send() {
    const msg = featureText.trim();
    if (!msg) {
      appAlert(t('settings.feature_too_short'), t('settings.feature_too_short_msg'));
      return;
    }
    if (!user) return;
    setFeatureState('sending');
    try {
      await submitFeatureRequest(user.id, msg, screenshotPath);
      setFeatureState('done');
      autoCloseRef.current = setTimeout(() => close(), 3000);
    } catch {
      setFeatureState('idle');
      appAlert(t('settings.feature_error'), t('settings.feature_error_msg'));
    }
  }

  const value = useMemo(() => ({ open }), []);

  return (
    <BugReportContext.Provider value={value}>
      {children}
      {visible && (
        <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
          <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
            <Pressable style={styles.modalOverlay} onPress={close} accessibilityRole="button">
              <Pressable style={[styles.modalSheet, { paddingBottom: insets.bottom + spacing.lg }]} onPress={() => {}}>
                <View style={styles.modalHandle} />

                {featureState === 'done' ? (
                  <View style={styles.doneWrap}>
                    <Ionicons name="checkmark-circle" size={56} color={colors.success} />
                    <Text style={styles.doneTitle}>{t('settings.feature_done_title')}</Text>
                    <Text style={styles.doneText}>{t('settings.feature_done_body')}</Text>
                    <Pressable onPress={close} style={styles.doneClose} accessibilityRole="button">
                      <Text style={styles.doneCloseText}>{t('common.close')}</Text>
                    </Pressable>
                  </View>
                ) : (
                  <>
                    <Text style={styles.modalTitle}>{t('settings.feature_modal_title')}</Text>
                    <Text style={styles.modalSubtitle}>{t('settings.feature_modal_subtitle')}</Text>
                    <TextInput
                      style={styles.featureInput}
                      value={featureText}
                      onChangeText={setFeatureText}
                      placeholder={t('settings.feature_placeholder')}
                      placeholderTextColor={colors.textMuted}
                      multiline
                      numberOfLines={5}
                      textAlignVertical="top"
                      maxLength={1000}
                      editable={featureState === 'idle'}
                      autoFocus
                    />
                    <Text style={styles.charCount}>{featureText.trim().length}/1000</Text>
                    <Pressable
                      onPress={pickScreenshot}
                      disabled={screenshotBusy || featureState !== 'idle'}
                      style={styles.attachRow}
                      accessibilityRole="button"
                    >
                      <Ionicons name={screenshotPath ? 'checkmark-circle' : 'image-outline'} size={16} color={screenshotPath ? colors.success : colors.primary} />
                      <Text style={[styles.attachText, screenshotPath && { color: colors.success }]}>
                        {screenshotBusy
                          ? t('settings.attach_uploading')
                          : screenshotPath
                          ? t('settings.attach_attached')
                          : t('settings.attach_screenshot')}
                      </Text>
                    </Pressable>
                    <Button
                      title={t('common.send')}
                      icon="send-outline"
                      onPress={() => void send()}
                      loading={featureState === 'sending'}
                      disabled={featureText.trim().length === 0}
                    />
                  </>
                )}
              </Pressable>
            </Pressable>
          </KeyboardAvoidingView>
        </View>
      )}
    </BugReportContext.Provider>
  );
}

const makeStyles = (colors: ReturnType<typeof useTheme>['colors']) =>
  StyleSheet.create({
    modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
    modalSheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      padding: spacing.lg,
      gap: spacing.md,
    },
    modalHandle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.border,
      alignSelf: 'center',
      marginBottom: spacing.xs,
    },
    modalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    modalSubtitle: { color: colors.textMuted, fontSize: fontSize.md, lineHeight: 21, marginTop: -spacing.xs },
    featureInput: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      padding: spacing.md,
      color: colors.text,
      fontSize: fontSize.md,
      minHeight: 120,
      backgroundColor: colors.surfaceAlt,
    },
    charCount: { color: colors.textMuted, fontSize: 11, textAlign: 'right', marginTop: -spacing.xs },
    attachRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: spacing.xs },
    attachText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    doneWrap: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xl },
    doneTitle: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700' },
    doneText: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center', lineHeight: 22 },
    doneClose: { marginTop: spacing.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.lg },
    doneCloseText: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' },
  });
